import React from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { alpha, Box, LinearProgress, Paper, styled, Typography } from '@mui/material';
import { Permission, Resource } from '@pepperhq/auth-client';

import { useAppSelector } from 'store/hooks';
import { AppStoreDetailsSection } from './setup/AppStoreDetailsSection';
import { AppLinksConfigurationSection } from './setup/AppLinksConfigurationSection';
import { WebAppLinksSection } from './setup/WebAppLinksSection';
import { useSetupTrackingLinks } from '../hooks/useSetupTrackingLinks';

const StyledLinearProgress = styled(LinearProgress)(({ theme }) => ({
    height: '10px',
    borderRadius: theme.shape.borderRadius
}));

const StyledBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(1, 2),
    backgroundColor: alpha(theme.palette.secondary.light, 0.3),
    color: theme.palette.secondary.main,
    borderRadius: theme.shape.borderRadius
}));

const ReadOnlyBanner = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1, 2),
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.text.secondary,
    borderRadius: theme.shape.borderRadius
}));

export const SetupTrackingLinksPage = () => {
    const { progress, isSetupComplete } = useSetupTrackingLinks();
    const { claims } = useAppSelector(state => state.auth);

    const isReadOnly = React.useMemo(
        () => !claims.hasPermission(Resource.Setting, Permission.write),
        [claims]
    );

    const formattedProgress = Math.round(progress * 100);

    return (
        <>
            {isSetupComplete && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                    <Box display="flex" flexDirection="row" gap={2} alignItems="center">
                        <CheckCircleIcon color="success" />
                        <Box display="flex" flexDirection="column">
                            <Typography variant="body1" color="success">
                                Setup complete — Campaign Links is fully enabled.
                            </Typography>
                            <Typography variant="body2">
                                Remember: a new app build must be distributed before app Campaign Links will
                                begin counting clicks. Schedule an app update to start tracking.
                            </Typography>
                        </Box>
                    </Box>
                </Paper>
            )}
            {!isSetupComplete && (
                <Box
                    display="flex"
                    flexDirection="column"
                    gap={2}
                    pb={6}
                    sx={{ maxWidth: '800px', mx: 'auto' }}
                >
                    <Typography variant="body1">
                        Complete the prerequisites below to start creating campaign links. You cannot create
                        links until setup is complete.
                    </Typography>
                    <Box display="flex" flexDirection="row" gap={2} alignItems="center">
                        <Box flexGrow={1}>
                            <StyledLinearProgress
                                variant="determinate"
                                value={formattedProgress}
                                aria-label="Export data"
                            />
                        </Box>
                        <Typography variant="body2">
                            {Intl.NumberFormat('en-GB', {
                                style: 'percent',
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0
                            }).format(progress)}{' '}
                            Complete
                        </Typography>
                    </Box>

                    <StyledBox>
                        <Typography variant="body2">A new app build is required for app links</Typography>
                        <Typography variant="caption">
                            Please ensure you request a new app build before using any app links. After this
                            new build, you can make as many app links as you want. You can start using web
                            links straightaway!
                        </Typography>
                    </StyledBox>

                    {isReadOnly && (
                        <ReadOnlyBanner>
                            <Typography variant="body2">
                                You have read-only access. Contact an Owner or Pepper Admin to make changes.
                            </Typography>
                        </ReadOnlyBanner>
                    )}

                    <AppStoreDetailsSection isReadOnly={isReadOnly} />
                    <AppLinksConfigurationSection isReadOnly={isReadOnly} />
                    <WebAppLinksSection isReadOnly={isReadOnly} />
                </Box>
            )}
        </>
    );
};
